import React, { useState, memo, useEffect, useRef, useContext, useCallback } from "react";
import { MapRef, Marker } from "react-map-gl";
import { Box, Icon, Text, Tooltip } from "@chakra-ui/react";
import {
  FaBatteryEmpty,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryQuarter,
  FaBatteryThreeQuarters,
} from "react-icons/fa";
import { Pin, PinValve, PinEnv, PinRain, PinTracker } from "./Pin";
import authContext from "../../context/AuthContext";
import PopupComponent from "./PopupComponent";
import { IData, IDevice } from "../../types/deviceInterface";

interface Props {
  devices: IDevice[];
  plotkey: keyof IData;
  referenceTimestamp?: Date | null;
  mapRef: React.RefObject<MapRef | null>;
  plotSettingsOverride?: {
    moisture_plot_minimum?: number;
    moisture_plot_maximum?: number;
    temperature_plot_minimum?: number;
    temperature_plot_maximum?: number;
    soil_conductivity_plot_minimum?: number;
    soil_conductivity_plot_maximum?: number;
  };
  fieldSpecificMode?: "none" | "moisture_status" | "moisture_index";
  focusedFieldId?: number;
  moistureStatusParams?: {
    moisture_limit_critical?: number;
    moisture_limit_damage?: number;
    moisture_limit_deficit?: number;
    moisture_limit_field_capacity?: number;
  };
  moistureIndexParams?: {
    management?: number;
    deficit?: number;
    upperManagement?: number;
  };
  moistureIndexParamsByFieldId?: Record<
    number,
    {
      management?: number;
      deficit?: number;
      upperManagement?: number;
    }
  >;
  popupPlotKey?: keyof IData;
}

const getBatteryStatus = (device: IDevice) => {
  const voltage = Number(device.data?.battery_voltage);
  const hasVoltage = Number.isFinite(voltage) && voltage > 0;

  if (!hasVoltage) return null;

  const icon =
    voltage <= 2.1
      ? FaBatteryEmpty
      : voltage <= 2.25
        ? FaBatteryQuarter
        : voltage <= 2.5
          ? FaBatteryHalf
          : voltage <= 2.75
            ? FaBatteryThreeQuarters
            : FaBatteryFull;

  return {
    icon,
    voltage,
    color: voltage <= 2.1 ? "red.500" : voltage <= 2.5 ? "orange.400" : "green.500",
    label: `${voltage.toFixed(2)} V`,
  };
};

const RenderDevicesComponent: React.FC<Props> = ({
  devices,
  plotkey,
  referenceTimestamp,
  mapRef,
  plotSettingsOverride,
  fieldSpecificMode = "none",
  focusedFieldId,
  moistureStatusParams,
  moistureIndexParams,
  moistureIndexParamsByFieldId,
  popupPlotKey,
}) => {
  const { userSettings } = useContext(authContext);
  const [popupInfo, setPopupInfo] = useState<(IDevice & { label_code?: string }) | null>(null);
  const referenceTimeMs = referenceTimestamp?.getTime() ?? Date.now();

  const rafRef = useRef<number | null>(null);
  const isAdjustingRef = useRef(false);

  const getColorParams = (pk: keyof IData): Record<string, number> => {
    if (pk === "soil_moisture" || pk === "soil_moisture_2" || pk === "soil_moisture_3") {
      return {
        min: plotSettingsOverride?.moisture_plot_minimum ?? userSettings.moisture_plot_minimum ?? 0,
        max:
          plotSettingsOverride?.moisture_plot_maximum ?? userSettings.moisture_plot_maximum ?? 100,
      };
    }
    if (
      pk === "soil_conductivity" ||
      pk === "soil_conductivity_2" ||
      pk === "soil_conductivity_3"
    ) {
      return {
        min:
          plotSettingsOverride?.soil_conductivity_plot_minimum ??
          userSettings.soil_conductivity_plot_minimum ??
          0,
        max:
          plotSettingsOverride?.soil_conductivity_plot_maximum ??
          userSettings.soil_conductivity_plot_maximum ??
          5,
      };
    }
    if (pk === "air_temperature" || pk === "air_temperature_2") {
      return {
        min:
          plotSettingsOverride?.temperature_plot_minimum ??
          userSettings.temperature_plot_minimum ??
          -10,
        max:
          plotSettingsOverride?.temperature_plot_maximum ??
          userSettings.temperature_plot_maximum ??
          40,
      };
    }
    if (pk === "soil_temperature") {
      return {
        min:
          plotSettingsOverride?.temperature_plot_minimum ??
          userSettings.temperature_plot_minimum ??
          -10,
        max:
          plotSettingsOverride?.temperature_plot_maximum ??
          userSettings.temperature_plot_maximum ??
          40,
      };
    }
    return { min: 0, max: 1 };
  };

  const moistureColorFn = (value: number | undefined, params: Record<string, number>) => {
    const low = [255, 0, 0];
    const mid = [0, 255, 0];
    const high = [0, 0, 255];
    if (value == null) return "#000";

    const step = Math.min(Math.max((value - params.min) / (params.max - params.min), 0), 1);
    const blend = (c1: number[], c2: number[], r: number) =>
      c1.map((c, i) => Math.round(c + r * (c2[i] - c)));

    const col = step <= 0.5 ? blend(low, mid, step * 2) : blend(mid, high, (step - 0.5) * 2);
    return `rgb(${col[0]},${col[1]},${col[2]})`;
  };

  const conductivityColorFn = (value: number | undefined, params: Record<string, number>) => {
    const low = [255, 0, 0];
    const mid = [255, 165, 0];
    const high = [0, 255, 0];
    if (value == null) return "#000";

    const step = Math.min(Math.max((value - params.min) / (params.max - params.min), 0), 1);
    const blend = (c1: number[], c2: number[], r: number) =>
      c1.map((c, i) => Math.round(c + r * (c2[i] - c)));

    const col = step <= 0.5 ? blend(low, mid, step * 2) : blend(mid, high, (step - 0.5) * 2);
    return `rgb(${col[0]},${col[1]},${col[2]})`;
  };

  const temperatureColorFn = (value: number | undefined, params: Record<string, number>) => {
    const low = [0, 0, 255];
    const mid = [255, 255, 255];
    const high = [255, 0, 0];
    if (value == null) return "#000";

    const step = Math.min(Math.max((value - params.min) / (params.max - params.min), 0), 1);
    const blend = (c1: number[], c2: number[], r: number) =>
      c1.map((c, i) => Math.round(c + r * (c2[i] - c)));

    const col = step <= 0.5 ? blend(low, mid, step * 2) : blend(mid, high, (step - 0.5) * 2);
    return `rgb(${col[0]},${col[1]},${col[2]})`;
  };

  const getColorFunction = (pk: keyof IData) => {
    if (
      pk === "soil_conductivity" ||
      pk === "soil_conductivity_2" ||
      pk === "soil_conductivity_3"
    ) {
      return conductivityColorFn;
    }
    if (pk === "air_temperature" || pk === "air_temperature_2" || pk === "soil_temperature") {
      return temperatureColorFn;
    }
    return moistureColorFn;
  };

  const moistureStatusColorFn = (
    value: number | undefined,
    params?: {
      moisture_limit_critical?: number;
      moisture_limit_damage?: number;
      moisture_limit_deficit?: number;
      moisture_limit_field_capacity?: number;
    },
  ) => {
    const critical = params?.moisture_limit_critical;
    const damage = params?.moisture_limit_damage;
    const deficit = params?.moisture_limit_deficit;
    const fieldCapacity = params?.moisture_limit_field_capacity;

    if (
      value === undefined ||
      critical === undefined ||
      damage === undefined ||
      deficit === undefined ||
      fieldCapacity === undefined
    ) {
      return "#000";
    }

    if (value <= critical) return "black";
    if (value <= damage) return "red";
    if (value <= deficit) return "rgb(255, 165, 0)";
    if (value <= fieldCapacity) return "rgb(0, 255, 0)";
    return "blue";
  };

  const moistureIndexColorFn = (
    value: number | undefined,
    params?: {
      management?: number;
      deficit?: number;
      upperManagement?: number;
    },
  ) => {
    if (value === undefined) {
      return "#000";
    }

    const management = params?.management;
    const deficit = params?.deficit;
    const upperManagement = params?.upperManagement;

    if (typeof management !== "number") {
      return value <= 0.5 ? "rgb(255, 0, 0)" : value <= 1 ? "rgb(68, 255, 68)" : "rgb(0, 0, 250)";
    }

    if (typeof deficit === "number") {
      if (value < deficit) return "rgb(255, 0, 0)";
      if (value < management) return "rgb(255, 165, 0)";
    } else if (value < management) {
      return "rgb(255, 0, 0)";
    }

    if (typeof upperManagement === "number" && value < upperManagement) {
      return "rgb(68, 255, 68)";
    }

    if (value <= 1) {
      return typeof upperManagement === "number" ? "rgb(68, 255, 250)" : "rgb(68, 255, 68)";
    }

    return "rgb(0, 0, 250)";
  };

  const setPopupInView = useCallback(
    (popupRootElement: HTMLDivElement | null) => {
      const mapInstance = mapRef.current?.getMap();
      if (!mapInstance || !popupRootElement) return;

      const mapContainer = mapInstance.getContainer();
      const mapRect = mapContainer.getBoundingClientRect();

      const popupShell = popupRootElement.closest(".mapboxgl-popup") as HTMLDivElement | null;
      const popupRect = (popupShell ?? popupRootElement).getBoundingClientRect();

      const paddingLeft = 16;
      const paddingRight = 16;
      const paddingTop = 16;
      const paddingBottom = 24;

      const overflowLeft = Math.max(0, mapRect.left + paddingLeft - popupRect.left);
      const overflowRight = Math.max(0, popupRect.right - (mapRect.right - paddingRight));
      const overflowTop = Math.max(0, mapRect.top + paddingTop - popupRect.top);
      const overflowBottom = Math.max(0, popupRect.bottom - (mapRect.bottom - paddingBottom));

      const shiftX = overflowRight - overflowLeft;
      const shiftY = overflowBottom - overflowTop;

      if (Math.abs(shiftX) < 1 && Math.abs(shiftY) < 1) {
        return;
      }

      if (isAdjustingRef.current) {
        return;
      }

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const currentCenterPx = mapInstance.project(mapInstance.getCenter());

        isAdjustingRef.current = true;

        mapInstance.easeTo({
          center: mapInstance.unproject([currentCenterPx.x + shiftX, currentCenterPx.y + shiftY]),
          duration: 300,
        });

        window.setTimeout(() => {
          isAdjustingRef.current = false;
        }, 350);
      });
    },
    [mapRef],
  );

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <>
      {devices.map((device, idx) => {
        if (device.device_type == 7) return null;
        if (device.latitude == null || device.longitude == null) return null;

        const val = device.data?.[plotkey] as number | undefined;
        const isStale =
          !!device.data?.timestamp &&
          referenceTimeMs - new Date(device.data.timestamp).getTime() > 21600000;

        const isFocusedFieldDevice =
          typeof focusedFieldId === "number" && Number(device.field_id) === Number(focusedFieldId);
        const batteryStatus = getBatteryStatus(device);

        let sensorColor = "#a3a3a3";

        if (fieldSpecificMode === "moisture_status") {
          if (isFocusedFieldDevice && !isStale && val != null) {
            sensorColor = moistureStatusColorFn(val, moistureStatusParams);
          }
        } else if (fieldSpecificMode === "moisture_index") {
          if (!isStale && val != null) {
            sensorColor = moistureIndexColorFn(
              val,
              moistureIndexParamsByFieldId?.[Number(device.field_id)] ??
                (isFocusedFieldDevice ? moistureIndexParams : undefined),
            );
          }
        } else {
          const fn = getColorFunction(plotkey);
          const params = getColorParams(plotkey);
          sensorColor = val != null && !isStale ? fn(val, params) : "#a3a3a3";
        }

        let PinComponent: React.FC<any>;
        let pinProps = {};

        switch (device.device_type) {
          case 3:
            PinComponent = PinEnv;
            pinProps = { pinColor: "#00cc52", iconColor: "white" };
            break;
          case 5:
          case 6:
            PinComponent = PinValve;
            pinProps = { pinColor: "#FF5722", iconColor: "white" };
            break;
          case 8:
            PinComponent = PinRain;
            pinProps = { pinColor: "#00ccb8", iconColor: "white" };
            break;
          case 9:
            PinComponent = PinTracker;
            pinProps = { pinColor: "#00a5a8", iconColor: "white" };
            break;
          default:
            PinComponent = Pin;
            pinProps = { color: sensorColor };
        }

        return (
          <Marker
            key={`${device.device_id}-${idx}`}
            latitude={device.latitude}
            longitude={device.longitude}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setPopupInfo(device);
            }}
            anchor="bottom"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              style={{ transform: "translateY(-4px)" }}
            >
              {(() => {
                let displayValue: number | undefined;
                let sign = "";

                if (device.device_type === 3) {
                  displayValue = device.data.air_temperature;
                  sign = "°C";
                } else if (device.device_type !== 5 && device.device_type !== 6) {
                  if (
                    plotkey === "soil_moisture" ||
                    plotkey === "soil_moisture_2" ||
                    plotkey === "soil_moisture_3"
                  ) {
                    displayValue =
                      device.data[plotkey] !== undefined ? Number(device.data[plotkey]) : undefined;
                    sign = "%";
                  } else if (plotkey === "soil_temperature" || plotkey === "air_temperature") {
                    displayValue =
                      device.data[plotkey] !== undefined ? Number(device.data[plotkey]) : undefined;
                    sign = "°C";
                  } else if (String(plotkey).startsWith("moisture_index")) {
                    const indexValue = device.data[plotkey];
                    displayValue = indexValue != null ? Number(indexValue) : undefined;
                    sign = "";
                  } else if (
                    plotkey === "soil_conductivity" ||
                    plotkey === "soil_conductivity_2" ||
                    plotkey === "soil_conductivity_3"
                  ) {
                    displayValue =
                      device.data[plotkey] !== undefined ? Number(device.data[plotkey]) : undefined;
                    sign = "dS/m";
                  }
                }

                if (displayValue === undefined) return null;

                return (
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    pointerEvents="none"
                    mb={1}
                    color="white"
                    textShadow="
                      -1px -1px 0 #000,
                      1px -1px 0 #000,
                      -1px  1px 0 #000,
                      1px  1px 0 #000
                    "
                  >
                    {String(plotkey).startsWith("moisture_index")
                      ? displayValue.toFixed(2)
                      : Math.round(displayValue)}{" "}
                    {sign}
                  </Text>
                );
              })()}
              <PinComponent size={30} {...pinProps} />
              {batteryStatus && batteryStatus.voltage <= 2.25 && (
                <Tooltip label={`Low battery: ${batteryStatus.label}`} hasArrow>
                  <Box position="absolute" top="-4px" right="-9px" display="flex">
                    <Icon as={batteryStatus.icon} color={batteryStatus.color} boxSize={3} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Marker>
        );
      })}

      {popupInfo && (
        <PopupComponent
          popupInfo={popupInfo}
          selectedPlotKey={popupPlotKey || plotkey}
          moistureIndexThresholds={
            moistureIndexParamsByFieldId?.[Number(popupInfo.field_id)] ??
            (Number(popupInfo.field_id) === focusedFieldId ? moistureIndexParams : undefined)
          }
          onClose={() => setPopupInfo(null)}
          onRendered={setPopupInView}
        />
      )}
    </>
  );
};

export default memo(
  RenderDevicesComponent,
  (prev, next) =>
    prev.devices === next.devices &&
    prev.plotkey === next.plotkey &&
    prev.referenceTimestamp?.getTime() === next.referenceTimestamp?.getTime() &&
    prev.plotSettingsOverride?.moisture_plot_minimum ===
      next.plotSettingsOverride?.moisture_plot_minimum &&
    prev.plotSettingsOverride?.moisture_plot_maximum ===
      next.plotSettingsOverride?.moisture_plot_maximum &&
    prev.plotSettingsOverride?.temperature_plot_minimum ===
      next.plotSettingsOverride?.temperature_plot_minimum &&
    prev.plotSettingsOverride?.temperature_plot_maximum ===
      next.plotSettingsOverride?.temperature_plot_maximum &&
    prev.plotSettingsOverride?.soil_conductivity_plot_minimum ===
      next.plotSettingsOverride?.soil_conductivity_plot_minimum &&
    prev.plotSettingsOverride?.soil_conductivity_plot_maximum ===
      next.plotSettingsOverride?.soil_conductivity_plot_maximum &&
    prev.fieldSpecificMode === next.fieldSpecificMode &&
    prev.focusedFieldId === next.focusedFieldId &&
    prev.moistureStatusParams?.moisture_limit_critical ===
      next.moistureStatusParams?.moisture_limit_critical &&
    prev.moistureStatusParams?.moisture_limit_damage ===
      next.moistureStatusParams?.moisture_limit_damage &&
    prev.moistureStatusParams?.moisture_limit_deficit ===
      next.moistureStatusParams?.moisture_limit_deficit &&
    prev.moistureStatusParams?.moisture_limit_field_capacity ===
      next.moistureStatusParams?.moisture_limit_field_capacity &&
    prev.moistureIndexParams?.management === next.moistureIndexParams?.management &&
    prev.moistureIndexParams?.deficit === next.moistureIndexParams?.deficit &&
    prev.moistureIndexParams?.upperManagement === next.moistureIndexParams?.upperManagement &&
    prev.moistureIndexParamsByFieldId === next.moistureIndexParamsByFieldId &&
    prev.popupPlotKey === next.popupPlotKey,
);
